<script lang="ts">
  import { fetchUnitCAT6Wiring, fetchUnitElectricalSummary, getErrorMessage } from '$lib/api';
  import { Download } from 'lucide-svelte';
  import { rcusQuery } from '$lib/queries/rcus';
  import { dinModulesForUnitQuery } from '$lib/queries/din_modules';
  import { inputDevicesQuery } from '$lib/queries/input_devices';
  import { roomsQuery } from '$lib/queries/rooms';
  import { unitsQuery } from '$lib/queries/units';
  import { router } from '$lib/router';
  import { showSuccess, showError } from '$lib/state/toast.svelte';
  import { addressScheduleCSV, downloadTextFile } from '../utils/engravingExport';
  import { generateElectricalLayoutPdf } from '../utils/electricalLayoutPdf';
  import type { InputDeviceResponse } from '$lib/types/input_device';
  import type { DinModuleResponse } from '$lib/types/din_module';
  import type { RCUResponse } from '$lib/types/rcu';

  const units = unitsQuery();
  const unitRcus = rcusQuery();
  const unitDinModules = dinModulesForUnitQuery();
  const unitDevices = inputDevicesQuery();
  const unitRooms = roomsQuery();

  let wiring: Record<string, unknown> | null = null;
  let loading = $state(true);
  let error = $state('');

  let unitId = $derived(router.unitId ?? 0);
  let unit = $derived((units.data ?? []).find((u) => u.id === unitId) ?? null);
  let rcusList = $derived((unitRcus.data ?? []).filter((r: RCUResponse) => r.unit === unitId));
  let unitRcuIds = $derived(new Set(rcusList.map((r) => r.id)));
  let unitDins = $derived(
    (unitDinModules.data ?? []).filter((d: DinModuleResponse) => unitRcuIds.has(d.rcu))
  );
  let unitDevicesList = $derived(unitDevices.data ?? []);

  async function load() {
    if (!unit) return;
    loading = true;
    error = '';
    try {
      // wiring = await fetchUnitCAT6Wiring(unit.id);
    } catch {
      error = 'Failed to load bus topology';
      wiring = null;
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    if (unitId > 0) load();
  });

  let addressCounts = $derived(
    (() => {
      const counts = new Map<string | number, number>();
      for (const dev of unitDevicesList) {
        const addr = dev.keys[0]?.address;
        if (addr == null) continue;
        counts.set(addr, (counts.get(addr) || 0) + 1);
      }
      for (const din of unitDins) {
        counts.set(din.address, (counts.get(din.address) || 0) + 1);
      }
      return counts;
    })()
  );

  function isDuplicateAddress(addr: number | string) {
    return (addressCounts.get(addr) ?? 0) > 1;
  }

  let branches = $derived(
    (() => {
      const map = new Map<
        number,
        { branch: number; devices: InputDeviceResponse[]; dins: DinModuleResponse[] }
      >();
      for (const dev of unitDevicesList) {
        const branch = dev.cat6_branch ?? 1;
        if (!map.has(branch)) map.set(branch, { branch, devices: [], dins: [] });
        map.get(branch)!.devices.push(dev);
      }
      map.set(0, { branch: 0, devices: [], dins: unitDins });
      return [...map.entries()].sort((a, b) => a[0] - b[0]).map(([, v]) => v);
    })()
  );

  function roomName(roomId: number) {
    return (unitRooms.data ?? []).find((r) => r.id === roomId)?.name ?? '—';
  }

  function deviceModel(dev: InputDeviceResponse) {
    return `KP-${dev.keys.length}BTN`;
  }

  interface AddressRow {
    device_type: string;
    room: string;
    branch: string | number;
    address: string | number | null;
    model: string;
  }

  function buildAddressRows(): AddressRow[] {
    const rows: AddressRow[] = [];
    for (const rcu of rcusList) {
      rows.push({
        device_type: 'RCU',
        room: '—',
        branch: 'main',
        address: '—',
        model: `RCU-${rcu.channel_count}CH`
      });
    }
    for (const din of unitDins) {
      rows.push({
        device_type: 'DIN',
        room: '—',
        branch: 'main',
        address: din.address,
        model: din.module_type
      });
    }
    for (const dev of unitDevicesList) {
      rows.push({
        device_type: dev.type === 'keycard' ? 'Keycard' : 'Keypad',
        room: roomName(dev.location_room ?? 0),
        branch: dev.cat6_branch ?? 1,
        address: dev.keys[0]?.address ?? '—',
        model: deviceModel(dev)
      });
    }
    return rows;
  }

  function exportAddressCSV() {
    // const csv = addressScheduleCSV(buildAddressRows());
    // downloadTextFile(csv, `${unit?.name ?? 'unit'}-address-schedule.csv`);
    // showSuccess('Address schedule downloaded');
  }

  async function exportWiringPdf() {
    if (!unit) return;
    try {
      const data = await fetchUnitElectricalSummary(unit.id);
      // await generateElectricalLayoutPdf(
      //   {
      //     project: { name: 'Project' },
      //     unit: unit,
      //     electricalSummary: data
      //   },
      //   `${unit.name}-wiring-handoff.pdf`
      // );
      showSuccess('Wiring handoff PDF downloaded');
    } catch (err: unknown) {
      showError(getErrorMessage(err));
    }
  }
</script>

<div class="p-6 space-y-6">
  <div class="flex items-center justify-between gap-3">
    <div>
      <h2 class="text-base font-medium text-text-primary">Bus topology</h2>
      <p class="text-xs text-text-muted">RCU → DIN modules and Cat-6 branches → keypads</p>
    </div>
    <div class="flex gap-2">
      <button
        type="button"
        class="inline-flex items-center gap-1 px-2 py-1 text-xs border border-border-default rounded hover:bg-bg-hover"
        onclick={exportAddressCSV}
      >
        <Download class="w-3.5 h-3.5" />
        Address CSV
      </button>
      <button
        type="button"
        class="inline-flex items-center gap-1 px-2 py-1 text-xs border border-border-default rounded hover:bg-bg-hover"
        onclick={exportWiringPdf}
      >
        <Download class="w-3.5 h-3.5" />
        Wiring PDF
      </button>
    </div>
  </div>

  {#if loading}
    <div class="text-sm text-text-muted">Loading…</div>
  {:else if error}
    <div class="text-sm text-state-error">{error}</div>
  {:else}
    <div class="border border-border-default rounded-lg p-4 space-y-4">
      {#each rcusList as rcu}
        <div class="flex items-start gap-3">
          <div
            class="rounded border border-border-strong bg-bg-rail px-3 py-2 text-sm font-medium min-w-[8rem]"
          >
            {rcu.name}
            <div class="text-2xs font-mono text-text-muted">{rcu.channel_count}ch RCU</div>
          </div>
          <div class="flex-1 space-y-2">
            {#if unitDins.length}
              <div class="text-2xs uppercase tracking-wide text-text-muted">
                DIN modules (main bus)
              </div>
              <div class="flex flex-wrap gap-2">
                {#each unitDins as din}
                  <div
                    class="rounded border px-2 py-1 text-xs font-mono {isDuplicateAddress(
                      din.address
                    )
                      ? 'border-state-error text-state-error bg-state-error/5'
                      : 'border-border-default text-text-secondary'}"
                  >
                    {din.module_type} @{din.address}
                  </div>
                {/each}
              </div>
            {/if}
          </div>
        </div>
      {:else}
        <p class="text-sm text-text-muted">No RCU configured</p>
      {/each}
    </div>

    {#each branches.filter((b) => b.branch !== 0) as branch}
      <div class="border border-border-default rounded-lg p-4">
        <div class="text-sm font-medium text-text-primary mb-3">
          Cat-6 Branch <span class="font-mono">{branch.branch}</span>
        </div>
        {#if branch.devices.length === 0}
          <p class="text-xs text-text-muted">No devices on this branch</p>
        {:else}
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {#each branch.devices as dev}
              <div
                class="rounded border px-3 py-2 text-xs {isDuplicateAddress(
                  dev.keys[0]?.address ?? ''
                )
                  ? 'border-state-error bg-state-error/5'
                  : 'border-border-default'}"
              >
                <div class="font-medium text-text-primary truncate">
                  {dev.type} · {roomName(dev.location_room ?? 0)}
                </div>
                <div class="font-mono text-text-muted mt-0.5">
                  @{dev.keys[0]?.address ?? '—'} · {deviceModel(dev)}
                </div>
                {#if isDuplicateAddress(dev.keys[0]?.address ?? '')}
                  <div class="text-state-error mt-1">Duplicate address</div>
                {/if}
              </div>
            {/each}
          </div>
        {/if}
      </div>
    {/each}

    <!-- {#if wiring?.topology?.repeater_branches?.length}
      <div class="rounded border border-state-warn/30 bg-state-warn/5 p-3 text-xs text-state-warn">
        RS485 repeater recommended — {wiring.topology.repeater_branches.length} repeater channel(s) in
        wiring plan.
      </div>
    {/if} -->
  {/if}
</div>
